import { useEffect, useState, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, Users, Tag, X, Plus, Loader2, Info, Trash2 } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface Donor {
  id: number;
  name: string;
  phone?: string;
  email?: string;
  totalDonated: number;
  donationCount: number;
  isMonthlyDonor: number;
  monthlyStatus?: string;
  lastDonationAt?: string;
  createdAt: string;
  tags: string[];
}

const TAG_COLORS: Record<string, string> = {
  '高額捐贈者': 'bg-purple-100 text-purple-700 border-purple-200',
  '長期支持者': 'bg-blue-100 text-blue-700 border-blue-200',
  '月捐用戶': 'bg-green-100 text-green-700 border-green-200',
  '新捐贈者': 'bg-amber-100 text-amber-700 border-amber-200',
  '回頭客': 'bg-teal-100 text-teal-700 border-teal-200',
  '沉睡用戶': 'bg-gray-100 text-gray-600 border-gray-200',
  '活躍用戶': 'bg-orange-100 text-orange-700 border-orange-200',
};

const ALL_TAGS = ['高額捐贈者', '長期支持者', '月捐用戶', '新捐贈者', '回頭客', '沉睡用戶', '活躍用戶'];

export default function AdminDonors() {
  const [donors, setDonors] = useState<Donor[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [receiptFilter, setReceiptFilter] = useState('');
  const [selectedDonor, setSelectedDonor] = useState<Donor | null>(null);
  const [newTag, setNewTag] = useState('');
  const [tagLoading, setTagLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'list' | 'rules'>('list');
  const limit = 20;

  const fetchDonors = useCallback(async () => {
    setLoading(true);
    const query = new URLSearchParams();
    query.set('limit', String(limit));
    query.set('offset', String((page - 1) * limit));
    if (search) query.set('search', search);
    if (tagFilter) query.set('tag', tagFilter);
    if (receiptFilter) query.set('receipt', receiptFilter);

    const res = await apiFetch<{ donors: Donor[]; total: number }>(`/api/admin/donors?${query.toString()}`);
    if (res.success && res.data) {
      setDonors(res.data.donors);
      setTotal(res.data.total);
    }
    setLoading(false);
  }, [page, search, tagFilter, receiptFilter]);

  useEffect(() => {
    fetchDonors();
  }, [fetchDonors]);

  const addTag = async (donorId: number, tag: string) => {
    setTagLoading(true);
    const res = await apiFetch(`/api/admin/donors/${donorId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tag }),
    });
    setTagLoading(false);
    if (res.success) {
      setDonors((prev) =>
        prev.map((d) => (d.id === donorId ? { ...d, tags: [...d.tags, tag] } : d))
      );
      if (selectedDonor?.id === donorId) {
        setSelectedDonor((prev) => (prev ? { ...prev, tags: [...prev.tags, tag] } : prev));
      }
      setNewTag('');
    }
  };

  const removeTag = async (donorId: number, tag: string) => {
    setTagLoading(true);
    const res = await apiFetch(`/api/admin/donors/${donorId}/tags/${encodeURIComponent(tag)}`, {
      method: 'DELETE',
    });
    setTagLoading(false);
    if (res.success) {
      setDonors((prev) =>
        prev.map((d) => (d.id === donorId ? { ...d, tags: d.tags.filter((t) => t !== tag) } : d))
      );
      if (selectedDonor?.id === donorId) {
        setSelectedDonor((prev) => (prev ? { ...prev, tags: prev.tags.filter((t) => t !== tag) } : prev));
      }
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`確定要刪除捐贈者「${name}」嗎？此操作無法撤銷。`)) return;
    const res = await apiFetch(`/api/admin/donors/${id}`, { method: 'DELETE' });
    if (res.success) {
      fetchDonors();
    } else {
      alert(res.error?.message || '刪除失敗');
    }
  };

  const totalPages = Math.ceil(total / limit);

  const tagRules = [
    { tag: '高額捐贈者', definition: '單筆 confirmed donation ≥ HK$1,000' },
    { tag: '長期支持者', definition: '累計 confirmed donations ≥ 5 次' },
    { tag: '月捐用戶', definition: 'is_monthly_donor = 1 且 monthly_status = active' },
    { tag: '新捐贈者', definition: '僅 1 次 confirmed donation' },
    { tag: '回頭客', definition: '≥ 2 次 confirmed donations' },
    { tag: '活躍用戶', definition: '最近捐款 < 30 天' },
    { tag: '沉睡用戶', definition: '最近捐款 > 90 天' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="w-6 h-6 text-brand-600" />
          捐贈者管理
        </h1>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          共 {total} 位捐贈者
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('list')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'list'
              ? 'bg-white text-brand-700 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          捐贈者列表
        </button>
        <button
          onClick={() => setActiveTab('rules')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'rules'
              ? 'bg-white text-brand-700 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          標籤定義
        </button>
      </div>

      {activeTab === 'list' && (
        <>
          {/* Search & Tag Filter */}
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="搜索姓名、WhatsApp 或電郵..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
              />
            </div>
            <select
              value={tagFilter}
              onChange={(e) => { setTagFilter(e.target.value); setPage(1); }}
              className="px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm bg-white"
            >
              <option value="">全部標籤</option>
              {ALL_TAGS.map((tag) => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
            <select
              value={receiptFilter}
              onChange={(e) => { setReceiptFilter(e.target.value); setPage(1); }}
              className="px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm bg-white"
            >
              <option value="">全部收據</option>
              <option value="requested">已索取收據</option>
              <option value="not-requested">未索取收據</option>
              <option value="issued">已發收據</option>
              <option value="has-request">已提交申請</option>
            </select>
          </div>

          {/* Donors Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">姓名</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">WhatsApp</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">總捐款</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">次數</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">月捐</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">標籤</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">最近捐款</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 w-16">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {donors.map((d) => (
                  <tr
                    key={d.id}
                    className="hover:bg-orange-50/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedDonor(d)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{d.name}</td>
                    <td className="px-4 py-3 text-gray-600">{d.phone || '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      HK${(d.totalDonated / 100).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{d.donationCount}</td>
                    <td className="px-4 py-3 text-center">
                      {d.isMonthlyDonor ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          是
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {d.tags.map((tag) => (
                          <span
                            key={tag}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${TAG_COLORS[tag] || 'bg-gray-100 text-gray-600 border-gray-200'}`}
                          >
                            {tag}
                          </span>
                        ))}
                        {d.tags.length === 0 && <span className="text-gray-300 text-xs">無標籤</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {d.lastDonationAt
                        ? new Date(d.lastDonationAt).toLocaleDateString('zh-HK')
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(d.id, d.name);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors"
                        title="刪除"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
                {donors.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                      暫無捐贈者記錄
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> 上一頁
            </button>
            <span className="text-sm text-gray-500">
              第 {page} / {totalPages} 頁
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              下一頁 <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

        </>
      )}

      {activeTab === 'rules' && (
        <div className="card shadow-sm border border-brand-100">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <Info className="w-4 h-4 text-brand-600" />
            <h3 className="font-bold text-gray-800">自動標籤規則</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500">
                  <th className="px-4 py-3 text-left font-medium">標籤</th>
                  <th className="px-4 py-3 text-left font-medium">定義</th>
                  <th className="px-4 py-3 text-left font-medium">觸發條件</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tagRules.map((rule) => (
                  <tr key={rule.tag} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${TAG_COLORS[rule.tag] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                        {rule.tag}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{rule.definition}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">捐款確認時自動計算</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
            這些標籤會在每次捐款被確認時自動重新計算。管理員也可以手動添加或移除標籤。
          </div>
        </div>
      )}

      {/* Tag Management Modal */}
      {selectedDonor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Tag className="w-5 h-5 text-brand-600" />
                管理標籤
              </h3>
              <button onClick={() => setSelectedDonor(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <p className="font-medium text-gray-900">{selectedDonor.name}</p>
              <p className="text-sm text-gray-500">{selectedDonor.phone || '無 WhatsApp'}</p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">當前標籤</p>
              <div className="flex flex-wrap gap-2">
                {selectedDonor.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${TAG_COLORS[tag] || 'bg-gray-100 text-gray-600 border-gray-200'}`}
                  >
                    {tag}
                    <button
                      onClick={() => removeTag(selectedDonor.id, tag)}
                      disabled={tagLoading}
                      className="hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {selectedDonor.tags.length === 0 && (
                  <span className="text-sm text-gray-400">暫無標籤</span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">添加標籤</p>
              <div className="flex flex-wrap gap-2">
                {ALL_TAGS.filter((t) => !selectedDonor.tags.includes(t)).map((tag) => (
                  <button
                    key={tag}
                    onClick={() => addTag(selectedDonor.id, tag)}
                    disabled={tagLoading}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border hover:opacity-80 transition-opacity ${TAG_COLORS[tag] || 'bg-gray-100 text-gray-600 border-gray-200'}`}
                  >
                    <Plus className="w-3 h-3" />
                    {tag}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  placeholder="自定義標籤..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && newTag.trim()) addTag(selectedDonor.id, newTag.trim()); }}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                />
                <button
                  onClick={() => newTag.trim() && addTag(selectedDonor.id, newTag.trim())}
                  disabled={!newTag.trim() || tagLoading}
                  className="px-4 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
                >
                  添加
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
